import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Card, Row, Col, Space, Statistic, Popconfirm, message, Spin, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, UserAddOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import PesoIcon from '../components/PesoIcon';
import { useAuth } from '../context/AuthContext';

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDeactivateCustomers = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [stats, setStats] = useState({ total_customers: 0, active_customers: 0, total_sales_value: 0, outstanding_balance: 0 });

  const fetchStats = async () => {
    try {
      const response = await client.get('/customers/stats');
      setStats(response.data);
    } catch {
      // stats are non-critical
    }
  };

  const fetchCustomers = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await client.get('/customers', {
        params: {
          search: search || undefined,
          limit: pagination.pageSize,
          page,
        },
      });
      setCustomers(response.data.data || []);
      setPagination((prev) => ({ ...prev, total: response.data.total || 0, current: page }));
    } catch (error) {
      message.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    fetchCustomers(1, value);
  };

  const handleAdd = () => {
    setSelectedCustomer(null);
    setIsEditing(false);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    const [legacyLastName = '', legacyFirstName = ''] = (record.name || '').split(/,(.*)/s);
    setSelectedCustomer(record);
    setIsEditing(true);
    form.setFieldsValue({
      last_name: record.last_name || legacyLastName.trim(),
      first_name: record.first_name || legacyFirstName.trim(),
      phone: record.phone,
      address: record.address,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await client.delete(`/customers/${id}`);
      message.success('Customer deactivated successfully');
      fetchCustomers(pagination.current, searchText);
      fetchStats();
    } catch (error) {
      message.error('Failed to deactivate customer');
    }
  };

  const handleSave = async (values) => {
    try {
      if (isEditing && selectedCustomer) {
        const payload = { ...values, code: selectedCustomer.code };
        await client.put(`/customers/${selectedCustomer.id}`, payload);
        message.success('Customer updated successfully');
      } else {
        const code = `CUST-${Date.now()}`;
        await client.post('/customers', { ...values, code });
        message.success('Customer created successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
      fetchCustomers(pagination.current, searchText);
      fetchStats();
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      if (validationErrors) {
        form.setFields(
          Object.entries(validationErrors).map(([name, errors]) => ({ name, errors }))
        );
      }
      message.error(error.response?.data?.message || 'Failed to save customer');
    }
  };

  const handleViewDetails = (record) => {
    navigate(`/customers/${record.id}`);
  };

  const handleTableChange = (newPagination) => {
    fetchCustomers(newPagination.current, searchText);
  };


  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (is_active) => (
        <Tag color={is_active ? 'green' : 'red'}>
          {is_active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Classification',
      key: 'classification',
      width: 120,
      render: (_, record) => record.customerDetail?.classification || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            ghost
            size="small"
            icon={<EyeOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              handleViewDetails(record);
            }}
            title="View Details"
          />
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              handleEdit(record);
            }}
            title="Edit"
          />
          {canDeactivateCustomers && (
            <Popconfirm
              title="Deactivate Customer"
              description="Are you sure you want to deactivate this customer?"
              onConfirm={(event) => {
                event?.stopPropagation?.();
                handleDelete(record.id);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={(event) => event.stopPropagation()}
                title="Deactivate"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h1>Customers</h1>

      {/* Stat Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Customers"
              value={stats.total_customers}
              prefix={<UserAddOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Customers"
              value={stats.active_customers}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Sales Value"
              value={stats.total_sales_value}
              prefix={<PesoIcon />}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Outstanding Balance"
              value={stats.outstanding_balance}
              prefix={<PesoIcon />}
              precision={2}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Search and Add Button */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        <Input.Search
          placeholder="Search by name or phone..."
          onSearch={handleSearch}
          style={{ maxWidth: '300px' }}
          allowClear
        />
        <Button type="primary" icon={<UserAddOutlined />} onClick={handleAdd}>
          Add Customer
        </Button>
      </div>

      {/* Customers Table */}
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={customers}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleViewDetails(record),
            style: { cursor: 'pointer' },
          })}
          scroll={{ x: 1000 }}
        />
      </Spin>

      {/* Add/Edit Modal */}
      <Modal
        title={isEditing ? 'Edit Customer' : 'Add New Customer'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => { setIsModalVisible(false); form.resetFields(); }}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={() => form.submit()}>
            {isEditing ? 'Save Changes' : 'Save Customer'}
          </Button>,
        ]}
        width={400}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            label="Last Name"
            name="last_name"
            rules={[
              { required: true, whitespace: true, message: 'Please enter the last name' },
              { max: 120, message: 'Last name must not exceed 120 characters' },
            ]}
          >
            <Input placeholder="e.g., Dela Cruz" />
          </Form.Item>

          <Form.Item
            label="First Name"
            name="first_name"
            rules={[
              { required: true, whitespace: true, message: 'Please enter the first name' },
              { max: 120, message: 'First name must not exceed 120 characters' },
            ]}
          >
            <Input placeholder="e.g., Juan" />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phone"
          >
            <Input placeholder="e.g., 09171234567" />
          </Form.Item>

          <Form.Item
            label="Address"
            name="address"
          >
            <Input.TextArea placeholder="Customer address" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
